import React, { useState } from 'react'
import { Button, Row, Col, Container } from 'reactstrap';
import { authEndpoints, endpoints } from '../../utils/Constants';
import { Input } from 'reactstrap'
import { useEffect } from 'react';
import LoadingAnimation from '../Form/LoadingAnimation';
import { useAlert } from '../../Services/AlertContext';
import Selector from '../Form/SelectorNoForm';
import useFetchSimple from '../../Services/useFetchSimple';
import useFetch from '../../Services/useFetch';
import { IAirportLookup } from '../../Models/IAirportLookup';
import { useFetchCached } from '../../Services/fetchApi';

interface rowProps {
    options: string[];
    newItem?: boolean;
    lookup: IAirportLookup;
    updateLookup: (item: IAirportLookup) => Promise<void>;
    saveNewLookup: (item: IAirportLookup) => Promise<void>;
    deleteLookup: (item: IAirportLookup) => Promise<void>;
}


const LookupRow = ({ options, newItem, lookup, updateLookup, saveNewLookup, deleteLookup }: rowProps) => {
    const { site, state, altState, iataCode } = lookup;
    const [newSite, setNewSite] = useState(site);
    const [deleteItem, setDeleteItem] = useState(false);
    const [selection, setSelection] = useState(state);
    const [altSelection, setAltSelection] = useState(altState || '');
    const [newIataCode, setNewIataCode] = useState(iataCode ?? "");
    const [saving, setSaving] = useState(false);

    /**
     * Determines whether the row is currently different than its lookup
     */
    const different = () => {
        if (!site && !state && !altState) return false;
        if (site !== newSite) return true;
        if (state !== selection) return true;
        if (altState || '' !== altSelection) return true;
        if (iataCode ?? "" !== newIataCode) return true;
        return false;
    };
    useEffect(() => {
        if (site) {
            setNewSite(site);
        }
    }, [site]);
    useEffect(() => {
        if (options && state) {
            setSelection(state);
        }
    }, [state, options]);
    useEffect(() => {
        if (options && altState) {
            setAltSelection(altState);
        }
    }, [altState, options]);
    useEffect(() => {
        if (iataCode) {
            setNewIataCode(iataCode);
        }
    }, [iataCode]);

    if (!lookup) return null;
    if (saving) return <LoadingAnimation height={50} />
    return <Row>
        <Col>
            <Input type="text" value={newSite} onChange={(e: { target: { value: string } }) => { setNewSite(e.target.value); }} />
        </Col>
        <Col>
            <Selector value={selection} onChange={({ value }) => { setSelection(value); }} options={options} />
        </Col>
        <Col>
            <Selector value={altSelection} onChange={({ value }) => {
                if (value === 'Clear') setAltSelection('');
                else setAltSelection(value);
            }} options={['Clear'].concat(options)} />
        </Col>
        <Col>
            <Input type="text" value={iataCode} onChange={(e: { target: { value: string } }) => { setNewIataCode(e.target.value) }} />
        </Col>
        <div style={{ visibility: different() || newItem ? "unset" : "hidden" }}>
            <Col>
                <Button onClick={async () => {
                    setSaving(true);
                    await (newItem ? saveNewLookup : updateLookup)({ id: lookup.id, site: newSite, state: selection, altState: altSelection ?? undefined, iataCode: newIataCode });
                    setSaving(false);
                }}>Save Changes</Button>
                <Button onClick={() => {
                    setNewSite(site);
                    setSelection(state);
                    setAltSelection(altState ?? '');
                    setNewIataCode(iataCode ?? "")
                }}>Cancel Changes</Button>
            </Col>
        </div>
        {!newItem ? <Button onClick={() => setDeleteItem(!deleteItem)}>{deleteItem ? "Cancel" : ""} Delete IAirportLookup</Button> : null}
        {(!newItem && deleteItem) ? <Button onClick={async () => {
            setSaving(true);
            await deleteLookup(lookup);
            setSaving(false);
        }}>Confirm Delete IAirportLookup</Button> : null}
    </Row>
}

interface State {
    code: string;
    country: string;
    id: string;
}

const AirportLookupEditor = () => {

    const [states, loading, error] = useFetch<State[]>(authEndpoints.getLocationStates);
    const [airportLookups, loadingAirportLookups, airportLookupsError, refresh] = useFetchCached<IAirportLookup[]>(endpoints.airportLookupAll, { forceRefresh: true });
    const [items, setItems] = useState(airportLookups instanceof Array ? airportLookups : []);
    const [options, setOptions] = useState(states instanceof Array ? states.map(state => state.code) : []);
    const fetch = useFetchSimple();
    const alert = useAlert();
    useEffect(() => {
        if (airportLookups instanceof Array) setItems(airportLookups);
    }, [airportLookups]);

    useEffect(() => {
        if (states instanceof Array) setOptions(states.map(state => state.code));
    }, [states]);

    const postSubmit = async (url: string, values: Object, verb: string) => {
        const options = { method: 'POST', body: JSON.stringify(values) };
        try {
            const result = await fetch(url, options).catch((e) => {
                alert({ title: 'Error saving setting', body: "Exception Result: " + e ? JSON.stringify(e) : 'No Error!' });
                throw e;
            });

            var ret = result.body;
            if (!ret.success) {
                alert({

                    title: "Error " + verb, body: <div>
                        <h5>{"Error saving setting"}</h5>
                        {ret.messages && ret.messages.length ? ret.messages.map(x => <p>{x}</p>) : null}
                    </div>
                    , onDismiss: () => { }
                });
            }
        }
        catch (error) {
            const errors = error?.body?.errors;
            const messages = errors ? Object.keys(errors).map((x, i) => {
                return <p key={i}>{errors[x].map(y => y)}</p>
            }) : null
            alert({
                title: 'Error saving IAirportLookup', body: <div>
                    <h3>Unexpected Result</h3>
                    {messages ? messages : JSON.stringify(error)}
                </div>
            });
        }
    };

    const updateLookup = async (e: IAirportLookup) => {
        await postSubmit(endpoints.updateAirportLookup, e, 'Updated');
        refresh();
    }
    const saveNewLookup = async (e: IAirportLookup) => {
        await postSubmit(endpoints.addAirportLookup, e, 'Saved');
        // Invalidate the lookups' cache
        refresh();
    }
    const deleteLookup = async (e: IAirportLookup) => {
        await postSubmit(endpoints.deleteAirportLookup, e, 'Deleted');
        setItems(items.filter(x => x.id !== e.id));
    }

    const newItem = () => {
        setItems(items.concat({ id: '', site: '', state: '' }));
    }

    useEffect(() => {
        console.log();
    }, [airportLookups, options, items]);

    return <React.Fragment>
        {loadingAirportLookups || loading ? <LoadingAnimation /> : null}
        {error ? <h6>Error loading states</h6> : null}
        {airportLookupsError ? <h3>Error</h3> : null}
        {<Container>
            {items.map(x => <LookupRow options={options} key={x.id} updateLookup={updateLookup} saveNewLookup={saveNewLookup} deleteLookup={deleteLookup} lookup={x} />)}
            <Button onClick={() => { newItem() }}>New</Button>
        </Container>}
    </React.Fragment>

}

export default AirportLookupEditor;